import { useEffect, useMemo, useState } from "react";
import { Check, Vote, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../common/Reveal";
import { api, formatApiError } from "../../lib/api";

const INTERESTS = [
    { key: "cotton_candy", label: "Cotton Candy" },
    { key: "popcorn", label: "Popcorn" },
    { key: "both", label: "Both" },
];

const chipCls = (on) =>
    `rounded-full px-4 py-2 font-oswald uppercase tracking-widest text-xs transition-colors duration-300 active:scale-95 ${
        on ? "bg-pink text-ink" : "border border-zinc-600 text-zinc-300 hover:border-pink hover:text-pink"
    }`;

const darkInput = "w-full rounded-2xl border border-zinc-700 bg-transparent px-5 py-3.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-pink";
const darkLabel = "block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400 mb-2";

const FlavorChips = ({ flavors, selected, onToggle, testid }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
        {flavors.map((f) => (
            <button key={f.id} type="button" onClick={() => onToggle(f.id)} data-testid={`${testid}-${f.id}`} className={chipCls(selected.includes(f.id))}>
                {f.name}
            </button>
        ))}
        {flavors.length === 0 && <p className="text-zinc-500 text-sm">Voting flavors are being picked — check back shortly.</p>}
    </div>
);

const ConsentRow = ({ checked, onChange, testid }) => (
    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid={testid}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-pink" data-testid={`${testid}-checkbox`} />
        <span className="text-xs text-zinc-400">Keep me on the Showtime Guest List for product drops, new flavors, events and offers.</span>
    </label>
);

const useVotingFlavors = () => {
    const [flavors, setFlavors] = useState([]);
    useEffect(() => {
        api.get("/flavors").then((res) => setFlavors((res.data || []).filter((f) => f.show_in_voting))).catch(() => {});
    }, []);
    return flavors;
};

const filterByInterest = (flavors, interest) =>
    flavors.filter((f) => interest === "both" || f.product_type === interest);

const VotePanel = ({ flavors, source }) => {
    const [interest, setInterest] = useState("cotton_candy");
    const [selected, setSelected] = useState([]);
    const [suggested, setSuggested] = useState("");
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const visible = useMemo(() => filterByInterest(flavors, interest), [flavors, interest]);
    const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const submit = async () => {
        if (!selected.length) {
            toast.error("Pick at least one flavor.");
            return;
        }
        if (consent && !email) {
            toast.error("Add your email to join the Guest List — or uncheck the box.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/flavor-votes", {
                product_interest: interest,
                flavor_ids: selected,
                suggested_flavor: suggested || undefined,
                email: email || undefined,
                marketing_consent: consent,
                source,
            });
            setDone(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="text-center py-10" data-testid="vote-confirmation">
                <p className="font-oswald uppercase text-3xl md:text-4xl text-pink">Your Vote Is In.</p>
                <p className="text-sm text-zinc-400 mt-3">Thanks for helping us pick the first drop.</p>
                <button onClick={() => { setDone(false); setSelected([]); setSuggested(""); }} data-testid="vote-again-button" className="mt-6 text-xs font-bold uppercase tracking-widest text-zinc-400 border-b border-zinc-600 pb-1 hover:text-pink hover:border-pink transition-colors">
                    Vote again
                </button>
            </div>
        );
    }

    return (
        <div data-testid="flavor-vote-panel">
            <div className="flex flex-wrap gap-2 mb-6" data-testid="vote-interest-toggle">
                {INTERESTS.map((i) => (
                    <button key={i.key} type="button" onClick={() => { setInterest(i.key); setSelected([]); }} data-testid={`vote-interest-${i.key}`} className={chipCls(interest === i.key)}>
                        {i.label}
                    </button>
                ))}
            </div>
            <FlavorChips flavors={visible} selected={selected} onToggle={toggle} testid="vote-flavor" />
            <div className="mt-6 grid gap-4">
                <div>
                    <label htmlFor="vote-suggested" className={darkLabel}>What flavor are we missing? (optional)</label>
                    <input id="vote-suggested" value={suggested} onChange={(e) => setSuggested(e.target.value)} maxLength={80} placeholder="Dream big." className={darkInput} data-testid="vote-suggested-input" />
                </div>
                <div>
                    <label htmlFor="vote-email" className={darkLabel}>Email (optional — only if you want updates)</label>
                    <input id="vote-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={darkInput} data-testid="vote-email-input" />
                </div>
                <ConsentRow checked={consent} onChange={setConsent} testid="vote-consent" />
                <button onClick={submit} disabled={loading} data-testid="cast-vote-button" className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors duration-300 active:scale-95 disabled:opacity-50 w-full sm:w-auto">
                    <Vote size={16} /> {loading ? "Casting..." : "Cast My Vote"}
                </button>
            </div>
        </div>
    );
};

const TesterPanel = ({ flavors, source }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ first_name: "", email: "", zip: "", product_interest: "cotton_candy", comment: "" });
    const [selected, setSelected] = useState([]);
    const [consent, setConsent] = useState(false);
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const visible = useMemo(() => filterByInterest(flavors, form.product_interest), [flavors, form.product_interest]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/flavor-testers", { ...form, flavor_ids: selected, marketing_consent: consent, source });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="text-center py-10" data-testid="tester-confirmation">
                <p className="font-oswald uppercase text-3xl md:text-4xl text-pink">You're On The List.</p>
                <p className="text-sm text-zinc-400 mt-3">We'll reach out if you're selected for an upcoming flavor test.</p>
            </div>
        );
    }

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} data-testid="tester-open-button" className="inline-flex items-center gap-2 rounded-full border-2 border-pink px-8 py-4 font-oswald uppercase tracking-widest text-sm text-pink hover:bg-pink hover:text-ink transition-colors duration-300 active:scale-95">
                <FlaskConical size={16} /> I'm Down To Test
            </button>
        );
    }

    return (
        <form onSubmit={submit} className="grid gap-4" data-testid="tester-form">
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="tester-name" className={darkLabel}>First Name</label>
                    <input id="tester-name" required value={form.first_name} onChange={set("first_name")} className={darkInput} data-testid="tester-name-input" />
                </div>
                <div>
                    <label htmlFor="tester-zip" className={darkLabel}>ZIP Code</label>
                    <input id="tester-zip" required value={form.zip} onChange={set("zip")} className={darkInput} data-testid="tester-zip-input" />
                </div>
            </div>
            <div>
                <label htmlFor="tester-email" className={darkLabel}>Email</label>
                <input id="tester-email" type="email" required value={form.email} onChange={set("email")} className={darkInput} data-testid="tester-email-input" />
            </div>
            <div>
                <span className={darkLabel}>Interested In</span>
                <div className="flex flex-wrap gap-2" data-testid="tester-interest-toggle">
                    {INTERESTS.map((i) => (
                        <button key={i.key} type="button" onClick={() => { setForm((f) => ({ ...f, product_interest: i.key })); setSelected([]); }} data-testid={`tester-interest-${i.key}`} className={chipCls(form.product_interest === i.key)}>
                            {i.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <span className={darkLabel}>Preferred Flavor(s)</span>
                <FlavorChips flavors={visible} selected={selected} onToggle={toggle} testid="tester-flavor" />
            </div>
            <div>
                <label htmlFor="tester-comment" className={darkLabel}>Anything you want us to know? (optional)</label>
                <textarea id="tester-comment" rows={2} value={form.comment} onChange={set("comment")} maxLength={500} className={darkInput} data-testid="tester-comment-input" />
            </div>
            <ConsentRow checked={consent} onChange={setConsent} testid="tester-consent" />
            <button type="submit" disabled={loading} data-testid="join-test-crew-button" className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors duration-300 active:scale-95 disabled:opacity-50 w-full sm:w-auto">
                <Check size={16} /> {loading ? "Joining..." : "Join The Test Crew"}
            </button>
        </form>
    );
};

export const ProductInterest = ({ source = "Website", testerCopy = "Help us test what's coming next. Join the Flavor Tester list, tell us what you'd like to try, and you may be selected to receive a sample in exchange for your feedback." }) => {
    const flavors = useVotingFlavors();
    return (
        <div className="mt-16 md:mt-20 grid gap-6 lg:grid-cols-2" data-testid="product-interest">
            <Reveal>
                <div className="rounded-3xl bg-ink tent-stripes text-white p-8 md:p-12 h-full">
                    <p className="font-manrope text-xs font-bold tracking-[0.35em] uppercase text-pink mb-3">Flavor Vote</p>
                    <h3 className="font-oswald uppercase text-3xl md:text-4xl leading-none mb-3">What Should Take The Stage First?</h3>
                    <p className="text-zinc-400 text-sm mb-8">Help us decide what Showtime drops first.</p>
                    <VotePanel flavors={flavors} source={source} />
                </div>
            </Reveal>
            <Reveal delay={0.1}>
                <div className="rounded-3xl bg-ink tent-stripes text-white p-8 md:p-12 h-full">
                    <p className="font-manrope text-xs font-bold tracking-[0.35em] uppercase text-pink mb-3">Flavor Tester</p>
                    <h3 className="font-oswald uppercase text-3xl md:text-4xl leading-none mb-3">Want To Be A Flavor Tester?</h3>
                    <p className="text-zinc-400 text-sm mb-8">{testerCopy}</p>
                    <TesterPanel flavors={flavors} source={source} />
                </div>
            </Reveal>
        </div>
    );
};
